import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import HubIcon from '@mui/icons-material/Hub'
import StorageIcon from '@mui/icons-material/Storage'
import { DataStoreMode, DataStoreReadNodeData, WorkflowNodeData } from '../types'
import { themeColor } from '../../../layout/theme/color'
import ExpressionTextField from '../autocomplete/ExpressionTextField'

interface DataStoreReadConfigProps {
  data: DataStoreReadNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const DataStoreReadConfig: FC<DataStoreReadConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const identifier = (data.identifier ?? '').trim()
  const isPlain = data.storageMode === 'plain'
  const hasQuery = Boolean((data.query ?? '').trim())

  const handleTopK = (raw: string) => {
    const parsed = Number.parseInt(raw, 10)
    if (Number.isNaN(parsed)) {
      onUpdate({ topK: 5 })
      return
    }
    const clamped = Math.max(1, Math.min(parsed, 50))
    onUpdate({ topK: clamped })
  }

  const warningText = !identifier
    ? translate('workflow.node.dataStoreMissingIdentifier')
    : !isPlain && !hasQuery
      ? translate('workflow.node.dataStoreMissingQuery')
      : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {warningText && <Alert severity="warning">{warningText}</Alert>}

      <Box>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: themeColor.slate60, mb: 0.5, display: 'block' }}
        >
          {translate('workflow.config.dataStoreMode')}
        </Typography>
        <ToggleButtonGroup
          value={data.storageMode ?? 'plain'}
          exclusive
          onChange={(_e, val: DataStoreMode | null) => val && onUpdate({ storageMode: val })}
          size="small"
          fullWidth
        >
          <ToggleButton value="vector" sx={{ textTransform: 'none', gap: 0.5 }}>
            <HubIcon sx={{ fontSize: 16 }} /> {translate('workflow.config.dataStoreModeVector')}
          </ToggleButton>
          <ToggleButton value="plain" sx={{ textTransform: 'none', gap: 0.5 }}>
            <StorageIcon sx={{ fontSize: 16 }} />{' '}
            {translate('workflow.config.dataStoreModePlain')}
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography
          variant="caption"
          sx={{ color: themeColor.slate60, mt: 0.5, display: 'block', lineHeight: 1.4 }}
        >
          {translate(
            isPlain
              ? 'workflow.config.dataStoreReadPlainHelp'
              : 'workflow.config.dataStoreReadVectorHelp',
          )}
        </Typography>
      </Box>

      <ExpressionTextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <ExpressionTextField
        required
        error={!identifier}
        label={translate('workflow.config.dataStoreIdentifier')}
        value={data.identifier ?? ''}
        onChange={(e) => onUpdate({ identifier: e.target.value })}
        size="small"
        fullWidth
        placeholder="paspoort-faq"
        helperText={translate('workflow.config.dataStoreIdentifierHelp')}
        InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
      />

      {isPlain ? (
        <>
          <ExpressionTextField
            label={translate('workflow.config.dataStoreKey')}
            value={data.key ?? ''}
            onChange={(e) => onUpdate({ key: e.target.value })}
            size="small"
            fullWidth
            placeholder="passport_processing_days"
            helperText={translate('workflow.config.dataStoreReadKeyHelp')}
            InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
          />
          <ExpressionTextField
            label={translate('workflow.config.dataStoreKeyword')}
            value={data.keyword ?? ''}
            onChange={(e) => onUpdate({ keyword: e.target.value })}
            size="small"
            fullWidth
            placeholder="{{ trigger.keyword }}"
            helperText={translate('workflow.config.dataStoreKeywordHelp')}
            InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
          />
          <TextField
            label={translate('workflow.config.dataStoreTopK')}
            type="number"
            value={data.topK ?? 5}
            onChange={(e) => handleTopK(e.target.value)}
            size="small"
            inputProps={{ min: 1, max: 50, step: 1 }}
            helperText={translate('workflow.config.dataStoreTopKHelp')}
          />
        </>
      ) : (
        <>
          <ExpressionTextField
            required
            error={!hasQuery}
            label={translate('workflow.config.dataStoreQuery')}
            value={data.query ?? ''}
            onChange={(e) => onUpdate({ query: e.target.value })}
            size="small"
            fullWidth
            multiline
            minRows={3}
            helperText={translate('workflow.config.dataStoreQueryHelp')}
          />

          <TextField
            label={translate('workflow.config.dataStoreTopK')}
            type="number"
            value={data.topK ?? 5}
            onChange={(e) => handleTopK(e.target.value)}
            size="small"
            inputProps={{ min: 1, max: 50, step: 1 }}
            helperText={translate('workflow.config.dataStoreTopKHelp')}
          />
        </>
      )}
    </Box>
  )
}

export default DataStoreReadConfig
